// client/src/components/reports/SharedReportPage.jsx
//
// /r/:token — a read-only report someone shared by link (Roadmap Phase 2).
// No sign-in. The server (routes/reportShareRoutes.js) runs the report live
// for the organization that made the link, with personal details masked,
// and answers 404 for links that were turned off, expired or never existed.
// Board reports (several sections) and single reports both render here.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaFileCsv, FaFilePdf } from 'react-icons/fa';
import ReportResult from './ReportResult';
import { getSharedReport, sharedDownloadUrl, errorText } from '../../services/reportsApi';
import '../analytics/PerformancePanel.css';
import './Reports.css';
import './SharedReportPage.css';

function day(value) {
  if (!value) return '';
  const d = new Date(String(value).replace(' ', 'T') + (String(value).includes('T') ? '' : 'Z'));
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
}

export default function SharedReportPage() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    getSharedReport(token)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((err) => {
        if (!cancelled) setError(errorText(err, t('reports.shared.notFound', 'This link is not valid. It may have expired or been turned off.')));
      });
    return () => { cancelled = true; };
  }, [token, t]);

  useEffect(() => {
    if (data && data.report) document.title = `${data.report.name} — ${data.report.organization || 'On-Screen'}`;
  }, [data]);

  const result = data && data.result;

  return (
    <div className="osr-page" data-testid="shared-report">
      <header className="osr-band">
        <span className="osr-brand">On-Screen</span>
        {data && data.report.organization && <span className="osr-org">{data.report.organization}</span>}
      </header>
      <main className="osr-main">
        {!data && !error && <p className="surface osr-loading">{t('reports.shared.loading', 'Loading the report…')}</p>}
        {error && (
          <section className="surface osr-card">
            <h1 className="osr-title">{t('reports.shared.unavailable', 'Report unavailable')}</h1>
            <p>{error}</p>
            <p className="surface-muted perf-small">{t('reports.shared.askSender', 'Ask the person who sent it for a new link.')}</p>
          </section>
        )}
        {data && (
          <>
            <section className="surface osr-card">
              <h1 className="osr-title">{data.report.name}</h1>
              {result && result.subtitle && <p className="osr-subtitle">{result.subtitle}</p>}
              {data.report.description && <p className="surface-muted perf-small">{data.report.description}</p>}
              <p className="surface-muted perf-small">
                {t('reports.shared.readOnly', 'A read-only report shared by {{org}}. Personal details are hidden.', { org: data.report.organization || t('reports.shared.anOrg', 'an organization') })}
                {data.share.expiresAt ? ` ${t('reports.shared.expires', 'This link works until {{date}}.', { date: day(data.share.expiresAt) })}` : ''}
              </p>
              <div className="rp-actions">
                <a className="lm-btn secondary" href={sharedDownloadUrl(token, 'pdf')}><FaFilePdf aria-hidden="true" /> {t('reports.shared.pdf', 'Download PDF')}</a>
                <a className="lm-btn secondary" href={sharedDownloadUrl(token, 'csv')}><FaFileCsv aria-hidden="true" /> {t('reports.shared.csv', 'Download CSV')}</a>
              </div>
            </section>
            {data.report.kind === 'composite'
              ? result.sections.map((s) => (
                <section key={s.key} className="surface osr-card" aria-labelledby={`osr-${s.key}`}>
                  <h2 id={`osr-${s.key}`} className="osr-section-title">{s.title}</h2>
                  {s.description && <p className="surface-muted perf-small">{s.description}</p>}
                  {s.skipped && <div className="note info">{s.skipped}</div>}
                  {s.error && <div className="note error">{s.error}</div>}
                  {s.result && <ReportResult result={s.result} kpiOnly />}
                </section>
              ))
              : (
                <section className="surface osr-card">
                  <ReportResult result={result} />
                </section>
              )}
          </>
        )}
      </main>
      <footer className="osr-foot">
        {t('reports.shared.footer', 'Made with On-Screen — event, ticketing and social media tools for nonprofits.')}{' '}
        <a href="/about">{t('reports.shared.learnMore', 'Learn more')}</a>
      </footer>
    </div>
  );
}
